import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { ApiClient } from '../../api/client';
import AccessReview from './AccessReview';
import { useAssistantUI } from './AssistantUI';
import ModelPicker from './ModelPicker';
import useAssistantViewport from './useAssistantViewport';
import { assistantPage, assistantPageLabel, type GatewaySnapshot } from './context';
import './PersonalAssistantDock.css';

function AssistantMark() {
  return <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 5.5h14v10H10l-5 3v-13Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" /><path d="m12 8 .7 2.2L15 11l-2.3.8-.7 2.2-.7-2.2L9 11l2.3-.8L12 8Z" fill="currentColor" /></svg>;
}

/** Owned by the authenticated shell. Route changes never replace the frame. */
export default function PersonalAssistantDock({ apiClient, ownerId }: { apiClient: ApiClient; ownerId: string }) {
  const location = useLocation();
  const { open, expanded, openChat, closeChat, toggleExpanded } = useAssistantUI();
  const [snapshot, setSnapshot] = useState<GatewaySnapshot | null>(null);
  const [connection, setConnection] = useState<'idle' | 'connecting' | 'ready'>('idle');
  const [gatewayOrigin, setGatewayOrigin] = useState<string | null>(null);
  const [attached, setAttached] = useState(false);
  const [frameRevision, setFrameRevision] = useState(0);
  const [canReopen, setCanReopen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dismissedFailure, setDismissedFailure] = useState<string | null>(null);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const reviewsButton = useRef<HTMLButtonElement>(null);
  const frame = useRef<HTMLIFrameElement>(null);
  const optionsButton = useRef<HTMLButtonElement>(null);
  const generation = useRef(0);
  const frameAcknowledged = useRef(false);
  const connectionAttempt = useRef<{ abort: AbortController; timer: ReturnType<typeof setTimeout> | null } | null>(null);
  const frameName = `personal-assistant-${ownerId}`;
  const optionsId = `${frameName}-options`;
  const page = assistantPage(location.pathname);
  const samePage = page && snapshot?.context?.kind === page.kind && snapshot.context.resource_id === page.resource_id;
  const visible = open;
  const viewport = useAssistantViewport(visible);
  useEffect(() => { setOptionsOpen(false); setReviewsOpen(false); }, [visible]);
  useEffect(() => {
    const element = dialog.current;
    if (open && element && !element.open) element.showModal();
    else if (!open && element?.open) element.close();
  }, [open]);
  const pending = (snapshot?.permissions?.filter(p => p.status === 'pending').length ?? 0)
    + (snapshot?.actions?.filter(a => a.status === 'proposed').length ?? 0)
    + (snapshot?.execution_access?.filter(a => a.status === 'pending').length ?? 0)
    + (snapshot?.integrations?.access.filter(a => a.status === 'pending').length ?? 0)
    + (snapshot?.integrations?.calls?.filter(a => a.status === 'proposed').length ?? 0);
  const hasReviews = !!(snapshot?.permissions?.some(p => ['pending', 'approved'].includes(p.status))
    || snapshot?.actions?.some(a => ['proposed', 'applied', 'stale'].includes(a.status)) || snapshot?.execution_access?.length || snapshot?.integrations?.access.length || snapshot?.integrations?.calls?.length);
  useEffect(() => { if (!hasReviews) setReviewsOpen(false); }, [hasReviews]);

  const refresh = useCallback(async () => {
    const current = ++generation.current;
    const value = await apiClient.get<GatewaySnapshot>('/api/assistant/gateway');
    if (generation.current === current) setSnapshot(value);
  }, [apiClient]);

  useEffect(() => {
    let disposed = false;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try { await refresh(); }
      catch { if (!disposed && visible) setError('The assistant connection could not be refreshed. Your conversation stays saved.'); }
      if (!disposed) timer = setTimeout(() => { void poll(); }, visible ? 5000 : 30000);
    };
    void poll();
    return () => { disposed = true; clearTimeout(timer); };
  }, [refresh, visible]);


  const clearConnectionAttempt = useCallback(() => {
    const attempt = connectionAttempt.current;
    connectionAttempt.current = null;
    if (attempt?.timer) clearTimeout(attempt.timer);
    attempt?.abort.abort();
  }, []);
  useEffect(() => () => clearConnectionAttempt(), [clearConnectionAttempt]);

  const connect = useCallback(async (reopen = false) => {
    if (connectionAttempt.current || !snapshot?.configured || !snapshot.origin || !snapshot.runtime_id || snapshot.status !== 'ready') return;
    const attempt = { abort: new AbortController(), timer: null as ReturnType<typeof setTimeout> | null };
    connectionAttempt.current = attempt;
    setConnection('connecting'); setError(null); setCanReopen(false);
    attempt.timer = setTimeout(() => {
      if (connectionAttempt.current !== attempt) return;
      clearConnectionAttempt(); setConnection('idle'); setCanReopen(frameAcknowledged.current);
      setError('Your conversation did not finish connecting. Reconnect to try again.');
    }, 15000);
    try {
      const target = new URL(snapshot.origin);
      if (target.origin === window.location.origin || target.origin !== snapshot.origin || target.pathname !== '/') {
        throw new Error('A separate assistant origin is required');
      }
      const issued = await apiClient.post<{ ticket: string; origin: string; runtime_id: string }>('/api/assistant/gateway/ticket', {});
      if (connectionAttempt.current !== attempt) return;
      if (issued.origin !== snapshot.origin || issued.runtime_id !== snapshot.runtime_id) throw new Error('Gateway binding changed');
      if (attached && frameAcknowledged.current && frame.current?.contentWindow && !reopen) {
        frame.current.contentWindow.postMessage({ type: 'mc.assistant.renew', ticket: issued.ticket, runtime_id: issued.runtime_id }, issued.origin);
      } else {
        const response = await fetch(`${issued.origin}/session`, {
          method: 'POST', credentials: 'include', mode: 'cors', redirect: 'error',
          referrerPolicy: 'origin', body: new URLSearchParams({ ticket: issued.ticket }), signal: attempt.abort.signal,
        });
        if (connectionAttempt.current !== attempt) return;
        if (!response.ok) throw new Error('Assistant session could not be established');
        frameAcknowledged.current = false;
        setGatewayOrigin(issued.origin); setFrameRevision(value => value + 1); setAttached(true);
      }
    } catch {
      if (connectionAttempt.current !== attempt) return;
      clearConnectionAttempt(); setConnection('idle'); setCanReopen(frameAcknowledged.current);
      setError('The assistant could not connect. Reconnect to check its current session.');
    }
  }, [apiClient, snapshot, attached, clearConnectionAttempt]);
  const connectRef = useRef(connect);
  useEffect(() => { connectRef.current = connect; }, [connect]);

  useEffect(() => {
    if (visible && snapshot?.configured && snapshot.status === 'ready' && !attached) void connectRef.current();
  }, [visible, snapshot?.configured, snapshot?.status, attached]);

  useEffect(() => {
    const receive = (event: MessageEvent) => {
      if (!snapshot?.origin || event.origin !== snapshot.origin || event.source !== frame.current?.contentWindow
        || event.data?.runtime_id !== snapshot.runtime_id) return;
      if (event.data.type === 'mc.assistant.ready') {
        frameAcknowledged.current = true; clearConnectionAttempt();
        setConnection('ready'); setError(null); setCanReopen(false);
      }
      if (event.data.type === 'mc.assistant.renew-needed') void connectRef.current();
      if (event.data.type === 'mc.assistant.session-expired') {
        clearConnectionAttempt(); setConnection('idle');
        setError('Your assistant session expired. Reconnect to continue the saved conversation.');
      }
    };
    window.addEventListener('message', receive);
    return () => window.removeEventListener('message', receive);
  }, [snapshot?.origin, snapshot?.runtime_id, clearConnectionAttempt]);

  const minimize = closeChat;
  const share = async () => {
    if (!page || busy) return;
    setBusy(true); setError(null);
    try { await apiClient.post('/api/assistant/gateway/context', { id: crypto.randomUUID(), ...page }); await refresh(); setOptionsOpen(false); }
    catch { setError('This page could not be shared. No additional resource access was granted.'); }
    finally { setBusy(false); }
  };
  const stopSharing = async () => {
    setBusy(true); setError(null);
    try { await apiClient.post('/api/assistant/gateway/context/clear', {}); await refresh(); setOptionsOpen(false); }
    catch { setError('The page-sharing change was not confirmed. Check the current context before continuing.'); }
    finally { setBusy(false); }
  };

  return <div className="personal-assistant-host">
    <button hidden={visible} className="assistant-launcher" type="button" aria-label="Open personal assistant" title="Ask your personal assistant" aria-expanded="false" onClick={openChat}>
      <AssistantMark /><span>Ask your assistant</span>{pending > 0 && <span className="assistant-launcher-count" aria-label={`${pending} reviews waiting`}>{pending}</span>}
    </button>
    <dialog ref={dialog} className={`assistant-dock${expanded ? ' assistant-dock--full' : ''}${viewport.keyboard ? ' assistant-dock--keyboard' : ''}`} style={viewport.style} aria-label="Personal assistant"
      onClose={() => { if (!dialog.current?.open) closeChat(); }} onCancel={event => {
        event.preventDefault();
        if (optionsOpen) { setOptionsOpen(false); optionsButton.current?.focus(); }
        else if (reviewsOpen) { setReviewsOpen(false); reviewsButton.current?.focus(); }
        else minimize();
      }} onKeyDown={event => {
        if (event.key === 'Escape' && (optionsOpen || reviewsOpen)) {
          event.preventDefault(); event.stopPropagation();
          if (optionsOpen) { setOptionsOpen(false); optionsButton.current?.focus(); }
          else { setReviewsOpen(false); reviewsButton.current?.focus(); }
        }
      }}>
      <header className="assistant-dock-heading"><div className="assistant-dock-identity"><span className="assistant-dock-avatar"><AssistantMark /></span>
        <strong>Assistant</strong><i className={`assistant-connection assistant-connection--${connection}`} title={connection === 'ready' ? 'Connected' : connection === 'connecting' ? 'Connecting' : 'Disconnected'} />
        </div>
        <div className="assistant-dock-window-controls">
          {hasReviews && <button ref={reviewsButton} className={`ghost assistant-requests-toggle${pending ? ' assistant-requests-toggle--pending' : ''}`} type="button" aria-label={pending ? `Review assistant requests (${pending})` : 'Assistant access and recent changes'}
            title="Access and requests" aria-expanded={reviewsOpen} onClick={() => { setReviewsOpen(value => !value); setOptionsOpen(false); }}>
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 4h14v16H5zM8 8h8M8 12h8M8 16h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
            {pending > 0 && <span className="assistant-request-count">{pending}</span>}
          </button>}
          <button ref={optionsButton} className="ghost assistant-options-toggle" type="button" aria-label="Page and model options" title="Page and model options" aria-expanded={optionsOpen} aria-controls={optionsId} onClick={() => { setOptionsOpen(value => !value); setReviewsOpen(false); }}>
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 7h16M4 17h16" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /><circle cx="9" cy="7" r="3" fill="var(--color-surface, #fff)" stroke="currentColor" strokeWidth="1.7" /><circle cx="15" cy="17" r="3" fill="var(--color-surface, #fff)" stroke="currentColor" strokeWidth="1.7" /></svg>
          </button>
          <button className="ghost assistant-expand-toggle" type="button" aria-label={expanded ? 'Restore assistant' : 'Expand assistant'} title={expanded ? 'Restore assistant' : 'Expand assistant'} onClick={() => { toggleExpanded(); setOptionsOpen(false); setReviewsOpen(false); }}>{expanded ? '↙' : '↗'}</button>
          <button className="ghost" type="button" aria-label="Minimize assistant" title="Minimize assistant" onClick={minimize}>—</button>
        </div>
      </header>
      {(optionsOpen || reviewsOpen) && <button className="assistant-controls-backdrop" aria-label="Close assistant controls" tabIndex={-1} onClick={() => { setOptionsOpen(false); setReviewsOpen(false); }} />}
      <div id={optionsId} className="assistant-chat-options" hidden={!optionsOpen} >
      <div className="assistant-page-context">
        {snapshot?.context ? <><span className="assistant-context-chip"><span aria-hidden="true">◈</span>{assistantPageLabel(snapshot.context)}{snapshot.context.resource_id ? ` · ${snapshot.context.resource_id.slice(0, 8)}` : ''}</span>
          <button className="ghost" type="button" disabled={busy} onClick={() => { void stopSharing(); }}>Stop sharing</button></> : <span className="assistant-context-hint">Your page contents stay private</span>}
        {page && snapshot?.configured && !samePage && <button className="assistant-share-page" type="button" disabled={busy} onClick={() => { void share(); }}>Use this page</button>}
      </div>
      {snapshot?.configured && snapshot.status === 'ready' && <ModelPicker apiClient={apiClient} visible={visible && optionsOpen} detailed={false} />}
      <Link className="assistant-manage-link" to="/personal-agent" onClick={minimize}>Connection and settings <span aria-hidden="true">↗</span></Link>
      </div>
      {error && <div className="assistant-dock-error" role="alert">{error}<button type="button" className="ghost" onClick={() => { void connectRef.current(); }}>Reconnect</button>
        {canReopen && <><span>Unsent text in the chat will be lost if you reopen. Saved history stays.</span><button type="button" className="ghost" onClick={() => { void connectRef.current(true); }}>Reopen saved conversation</button></>}
      </div>}
      {!error && snapshot?.model_failure && snapshot.model_failure.id !== dismissedFailure && <div className="assistant-dock-error" role="alert">
        {snapshot.model_failure.message}
        {snapshot.model_failure.code === 'assistant_budget_exceeded' && <Link to="/settings/models" onClick={minimize}>Review model access</Link>}
        <button className="ghost" type="button" onClick={() => setDismissedFailure(snapshot.model_failure!.id)}>Dismiss</button>
      </div>}
      {!snapshot ? <div className="assistant-dock-welcome"><span className="assistant-loading-mark" /><h2>Opening your assistant</h2><p>Finding your saved conversation…</p></div>
        : !snapshot.configured ? <div className="assistant-dock-welcome"><span className="assistant-welcome-mark"><AssistantMark /></span><h2>A little help, right here</h2>
          <p>Your personal OpenClaw connection is being prepared. Once connected, this same conversation will follow you across Mission Control.</p>
          <p>Ask about a page, grant access to a resource when needed, and review each proposed change.</p>
        </div> : snapshot.status !== 'ready' ? <div className="assistant-dock-welcome"><h2>Assistant paused</h2><p>Your conversation is saved. Resume the assistant to use its tools.</p>
          <button onClick={() => { void apiClient.post('/api/assistant/lifecycle', { paused: false }).then(refresh); }}>Resume assistant</button></div> : null}
      {attached && <iframe key={frameRevision} ref={frame} name={frameName} hidden={!snapshot?.configured || snapshot.status !== 'ready'} title="OpenClaw personal conversation" className="assistant-gateway-frame"
        sandbox="allow-scripts allow-same-origin allow-forms allow-downloads" allow="clipboard-write; fullscreen" referrerPolicy="no-referrer" src={`${gatewayOrigin}/?session=agent%3Amain%3Amain`} />}
      {snapshot?.configured && <AccessReview permissions={snapshot.permissions ?? []} actions={snapshot.actions ?? []} executionAccess={snapshot.execution_access ?? []} integrations={snapshot.integrations} apiClient={apiClient} refresh={refresh} open={reviewsOpen} onOpenChange={setReviewsOpen} onOpen={() => setOptionsOpen(false)} />}
    </dialog>
  </div>;
}
